import { useState, useRef, useEffect, useCallback } from 'react';
import { SpeakerWaveIcon } from '@heroicons/react/24/solid';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import { getSentencesByIds } from '../../firebase/words';
import { getSound } from '../../firebase/sounds';

const ExamplePlayButton = ({ sentenceId, compact = false }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const { audioElement, isPlaying: mainIsPlaying } = useAudioPlayer();

  // Reset state and clean up audio when sentence changes or component unmounts
  useEffect(() => {
    setAudioUrl(null);
    setIsLoading(false);
    setIsPlaying(false);
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [sentenceId]);

  // Stop when main word audio starts playing
  useEffect(() => {
    if (mainIsPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [mainIsPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  const playAudio = useCallback((url) => {
    if (audioRef.current) audioRef.current.pause();
    if (audioElement) audioElement.pause();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.addEventListener('ended', () => setIsPlaying(false));
    setIsPlaying(true);
    audio.play().catch(() => setIsPlaying(false));
  }, [audioElement]);

  const handleClick = useCallback(async () => {
    if (isLoading) return;

    // Already playing: restart from beginning
    if (isPlaying && audioRef.current) {
      audioRef.current.currentTime = 0;
      return;
    }

    // Cached URL: play immediately
    if (audioUrl) {
      playAudio(audioUrl);
      return;
    }

    // First click: fetch sentence details then audio
    setIsLoading(true);
    try {
      const sentences = await getSentencesByIds([sentenceId], null);
      if (!sentences?.[0]?.sound) { setIsLoading(false); return; }
      const url = await getSound(sentences[0].sound, 'sentences');
      setAudioUrl(url);
      setIsLoading(false);
      playAudio(url);
    } catch {
      setIsLoading(false);
    }
  }, [isLoading, isPlaying, audioUrl, sentenceId, playAudio]);

  if (!sentenceId) return null;

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`mt-4 flex items-center gap-1.5 rounded-full border transition-all
        ${compact ? 'text-xs px-3 py-1' : 'text-sm px-4 py-1.5'}
        ${isPlaying
          ? 'text-primary border-primary'
          : 'text-content-secondary border-border hover:text-content-primary hover:border-primary/50'
        }
        disabled:opacity-60 disabled:cursor-default`}
    >
      {isLoading ? (
        <svg
          className={`animate-spin flex-shrink-0 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <SpeakerWaveIcon className={`flex-shrink-0 ${compact ? 'h-3 w-3' : 'h-4 w-4'} ${isPlaying ? 'animate-pulse' : ''}`} />
      )}
      <span>Hear in context</span>
    </button>
  );
};

export default ExamplePlayButton;
